import { Link } from 'react-router-dom';
import { ArrowRight, Award, BookOpen, Mic, Heart } from 'lucide-react';

const DR_GRIFFIN_PHOTO = "https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/xfj6pmgv_Gemini_Generated_Image_twtrvgtwtrvgtwtr.png";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a5fde4e2-7c05-4d6f-9f85-02b21747978d/artifacts/1jxg5966_grief%20coaching%20logo.png";

export default function About() {
  const credentials = [
    {
      icon: Award,
      title: '30+ Years Leadership',
      description: 'Decades of experience guiding teams and organizations through transformation.'
    },
    {
      icon: BookOpen,
      title: '20+ Years in Education',
      description: 'Deep expertise in higher education and adult learning methodologies.'
    },
    {
      icon: Mic,
      title: 'Keynote Speaker',
      description: 'Sought-after speaker on grief, resilience, and purpose-driven leadership.'
    },
    {
      icon: Heart,
      title: 'Lived Experience',
      description: 'Personal journey through grief transformed into mission to help others.'
    }
  ];

  return (
    <div className="overflow-hidden" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#FAFAF8] relative" data-testid="about-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            {/* Image */}
            <div className="lg:col-span-5">
              <div className="relative animate-slide-in-left">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C9A962]/20 to-transparent rounded-3xl" />
                <img 
                  src={DR_GRIFFIN_PHOTO}
                  alt="Dr. Angelia Griffin"
                  className="relative rounded-2xl shadow-2xl w-full"
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="lg:col-span-7 animate-slide-in-right">
              <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
                About Dr. Griffin
              </span>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1B365D] mb-6 leading-tight">
                Grief Coach, Educator &
                <span className="block text-[#2D7D7B]">Keynote Speaker</span>
              </h1>
              <div className="section-divider" />
              <p className="text-lg text-[#1B365D]/80 leading-relaxed mb-6 mt-6">
                Dr. Angelia Griffin helps high-functioning leaders and communities transform loss into clarity, movement, and renewed purpose.
              </p>
              <p className="text-[#1B365D]/70 leading-relaxed">
                After the tragic loss of her son, William Donald Paul II, Dr. Griffin transformed personal grief into a life's mission—founding IGMA, Inc., a 501(c)(3) nonprofit, and AGC LLC Grief Coaching.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-story">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <img 
                src={LOGO_URL}
                alt="Grief Coaching Logo"
                className="h-20 mx-auto mb-8"
              />
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1B365D] mb-4">
                Turning Pain Into Purpose
              </h2>
              <div className="section-divider mx-auto" />
            </div>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-[#1B365D]/80 leading-relaxed mb-6">
                With over 30 years of leadership experience and more than 20 years in higher education, Dr. Griffin combines academic rigor with lived experience to guide individuals through acute, delayed, and hidden grief using a movement- and meaning-based process.
              </p>
              
              <p className="text-lg text-[#1B365D]/80 leading-relaxed mb-6">
                Her approach isn't about forgetting or "moving on"—it's about integrating loss into a new chapter of life filled with purpose and meaning. Like the Japanese art of Kintsugi, where broken pottery is repaired with gold, Dr. Griffin helps her clients find the gold in their brokenness.
              </p>
              
              <p className="text-lg text-[#1B365D]/80 leading-relaxed">
                Dr. Griffin is the author of <em className="text-[#2D7D7B] font-medium">"Turning In Is The Only Way Out"</em> and host of the weekly talk show <em className="text-[#2D7D7B] font-medium">"All That Matters."</em>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Grid */}
      <section className="py-24 md:py-32 bg-[#1B365D] relative" data-testid="about-credentials">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-[#C9A962] mb-4 block">
              Credentials & Experience
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white">
              Why Work With Dr. Griffin
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {credentials.map((credential, index) => (
              <div 
                key={credential.title}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-[#C9A962]/50 transition-colors"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-[#C9A962]/20 flex items-center justify-center mb-6">
                  <credential.icon className="w-7 h-7 text-[#C9A962]" />
                </div>
                <h3 className="font-serif text-xl font-bold text-white mb-3">
                  {credential.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {credential.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Organizations */}
      <section className="py-24 md:py-32 bg-[#F5F5F0]" data-testid="about-organizations">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* IGMA Inc */}
            <div className="service-card">
              <span className="tier-badge tier-badge-1 mb-4">Nonprofit</span>
              <h3 className="font-serif text-2xl font-bold text-[#1B365D] mb-4">
                IGMA, Inc.
              </h3>
              <p className="text-[#1B365D]/70 leading-relaxed mb-4">
                A 501(c)(3) nonprofit organization committed to empowering others through training and community awareness initiatives. IGMA stands as a testament to transforming personal tragedy into collective healing.
              </p>
              <p className="text-sm text-[#C9A962] font-medium">
                "I Give Myself Away"
              </p>
            </div>
            
            {/* AGC LLC */}
            <div className="service-card">
              <span className="tier-badge tier-badge-2 mb-4">Coaching Practice</span>
              <h3 className="font-serif text-2xl font-bold text-[#1B365D] mb-4">
                AGC LLC Grief Coaching
              </h3>
              <p className="text-[#1B365D]/70 leading-relaxed mb-4">
                Professional grief coaching services for individuals, groups, and organizations. AGC LLC provides structured, outcome-based support that honors your journey while moving you toward renewed purpose.
              </p>
              <p className="text-sm text-[#C9A962] font-medium">
                Professional Grief Coaching Services
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#2D7D7B] relative" data-testid="about-cta">
        <div className="absolute inset-0 grain-overlay" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Dr. Griffin is ready to guide you through your grief toward renewed purpose.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/programs" className="btn-secondary border-white text-white hover:bg-white hover:text-[#2D7D7B] inline-flex items-center justify-center gap-2" data-testid="about-cta-services">
                Explore Programs
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="https://griefassessment.agcthefixer.info/contact-724806" target="_blank" rel="noopener noreferrer" className="bg-white text-[#2D7D7B] hover:bg-[#F5F5F0] px-8 py-4 rounded-full font-bold transition-colors inline-flex items-center justify-center gap-2" data-testid="about-cta-contact">
                Get In Touch
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
